// =============================================================================
// Web LanguageSwitcher — SC-008
//
// Accessible <select>-based language switcher for the web shells (admin,
// vendor). Reads/writes the active locale via the shared i18n context. Styled
// with the design-token CSS variables so it inherits theme + dark-mode. Web
// only (DOM); the mobile shell ships its own RN switcher.
// =============================================================================
import type { ReactElement } from 'react';
import { LOCALES, isLocale, localeLabel, useI18n } from './index';

export interface LanguageSwitcherProps {
  /** Optional test id override; defaults to `language-switcher`. */
  readonly testId?: string;
}

export function LanguageSwitcher({ testId = 'language-switcher' }: LanguageSwitcherProps): ReactElement {
  const { locale, setLocale, t } = useI18n();
  return (
    <label
      data-testid={testId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {t('shell.language')}
      </span>
      <select
        aria-label={t('shell.language')}
        data-testid={`${testId}-select`}
        value={locale}
        onChange={(e) => {
          const next = e.target.value;
          if (isLocale(next)) setLocale(next);
        }}
        style={{
          background: 'var(--color-bg-muted)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 4,
          padding: '4px 8px',
          fontSize: 'var(--font-size-xs)',
          cursor: 'pointer',
        }}
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {localeLabel(l)}
          </option>
        ))}
      </select>
    </label>
  );
}
